import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Printer, Trash2, Truck } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import ChallanForm from '../../components/sales/ChallanForm';
import api from '../../lib/api';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') {
        return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      }
      return String(first);
    }
    return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  return `Request failed with status ${status}`;
};

const DeliveryChallans = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [challans, setChallans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) {
      navigate('/login');
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchChallans();
  }, [navigate, statusFilter]);

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/delivery-challans?${params}`);
      setChallans(response.data);
    } catch (err) {
      console.error('Error fetching challans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDispatch = async (challan) => {
    if (!confirm(`Dispatch challan ${challan.challan_number}? Stock will be deducted.`)) return;
    try {
      const res = await api.post(`/delivery-challans/${challan.id}/dispatch`);
      alert(res.data.message || 'Challan dispatched');
      fetchChallans();
    } catch (err) {
      alert(`Failed to dispatch: ${getErrorMessage(err)}`);
      console.error('Dispatch error:', err);
    }
  };

  const handleCancel = async (challan) => {
    const msg =
      challan.status === 'dispatched'
        ? `Cancel challan ${challan.challan_number}? Stock will be restored.`
        : `Cancel challan ${challan.challan_number}?`;
    if (!confirm(msg)) return;
    try {
      const res = await api.post(`/delivery-challans/${challan.id}/cancel`);
      alert(res.data.message || 'Challan cancelled');
      fetchChallans();
    } catch (err) {
      alert(`Failed to cancel: ${getErrorMessage(err)}`);
    }
  };

  const handleDelete = async (challan) => {
    if (challan.status === 'dispatched') {
      alert('Dispatched challans cannot be deleted. Cancel it instead.');
      return;
    }
    if (!confirm(`Delete challan ${challan.challan_number}?`)) return;
    try {
      await api.delete(`/delivery-challans/${challan.id}`);
      fetchChallans();
    } catch (err) {
      alert(`Failed to delete: ${getErrorMessage(err)}`);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchChallans();
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="challans-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
        }`}
      >
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]"
              data-testid="challans-title"
            >
              Delivery Challans
            </h1>
            <button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors flex items-center gap-2"
              data-testid="add-challan-button"
            >
              <Plus className="w-5 h-5" />
              New Challan
            </button>
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm mb-6 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1a1a2e] mb-2">
                  Status Filter
                </label>
                <select
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">Loading...</div>
          ) : challans.length === 0 ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center">
              <p className="text-[#6b7280] mb-4">No delivery challans yet</p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg px-4 py-2 font-medium transition-colors"
              >
                Create Your First Challan
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="challans-table">
                <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                  <tr>
                    <th className="py-3 px-4">Challan #</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Vehicle</th>
                    <th className="py-3 px-4">Driver</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {challans.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]"
                      data-testid={`challan-row-${c.id}`}
                    >
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {c.challan_number}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {c.customer?.name || 'N/A'}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">
                        {new Date(c.issue_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{c.vehicle_no || '—'}</td>
                      <td className="py-3 px-4 font-normal text-[#1a1a2e]">{c.driver_name || '—'}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-sm text-xs font-medium ${
                            c.status === 'dispatched'
                              ? 'bg-[#DBEAFE] text-[#1E40AF]'
                              : c.status === 'cancelled'
                              ? 'bg-[#FEE2E2] text-[#991B1B]'
                              : 'bg-[#F5F5F5] text-[#6b7280]'
                          }`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap items-center">
                          {c.status === 'draft' && (
                            <button
                              onClick={() => handleDispatch(c)}
                              className="flex items-center gap-1 text-[#4A7AE0] hover:underline text-xs font-medium"
                              data-testid={`dispatch-${c.id}`}
                            >
                              <Truck className="w-3 h-3" /> Dispatch
                            </button>
                          )}
                          {c.status !== 'draft' && (
                            <button
                              onClick={() =>
                                window.open(
                                  `/sales/delivery-challans/${c.id}/print`,
                                  '_blank'
                                )
                              }
                              className="flex items-center gap-1 text-[#4A7AE0] hover:bg-[#EFF6FF] border border-[#4A7AE0] rounded-lg px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`print-${c.id}`}
                              title="Print Challan"
                            >
                              <Printer className="w-3 h-3" /> Print
                            </button>
                          )}
                          {c.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancel(c)}
                              className="text-[#EF4444] hover:underline text-xs font-medium"
                              data-testid={`cancel-${c.id}`}
                            >
                              Cancel
                            </button>
                          )}
                          {c.status === 'draft' && (
                            <button
                              onClick={() => handleDelete(c)}
                              className="flex items-center gap-1 text-[#EF4444] hover:bg-[#FEE2E2] border border-[#EF4444] rounded px-2 py-0.5 text-xs font-medium transition-colors"
                              data-testid={`delete-${c.id}`}
                              title="Delete Challan"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showForm && <ChallanForm onClose={() => setShowForm(false)} onSuccess={handleFormSuccess} />}
    </div>
  );
};

export default DeliveryChallans;
